/**
 * Real-money shop catalog, shared by the Cloudflare server (which charges
 * these prices through Stripe and grants these items) and the web client
 * (which shows them). Everything here is cosmetic or a thank-you: nothing
 * changes battle stats.
 *
 * `grants` are cosmetic ids (frontend/src/game/data/cosmetics.ts),
 * "supporter:N" badge levels (1 = bronze … 4 = diamond) or "heroslot:N"
 * (one more hero slot, frontend/src/game/systems/heroSlots.ts).
 */

import { HERO_LOOKS, LOOK_THB, lookItem } from './looks';

/** set = a themed bundle of drawing stamps, inks and a victory stamp; slot = one more hero slot; look = a ready-made hero (shared/looks.ts). */
/** Most products one cart may hold. */
export const MAX_CART = 10;
/** A cart purchase is stored as its skus joined with this ("pack:neon+set:knight"). */
export const CART_JOIN = '+';

/** The skus of a purchase: one product, or every product of a cart. */
export function cartSkus(sku: string): string[] {
  return sku.split(CART_JOIN).filter(Boolean);
}

export type ShopKind = 'pens' | 'stamp' | 'support' | 'set' | 'slot' | 'look';

export interface ShopProduct {
  sku: string;
  kind: ShopKind;
  name: string;
  note: string;
  /** Price in Thai baht (whole baht). */
  priceTHB: number;
  grants: string[];
}

export const SHOP_CURRENCY = 'thb';

export const SHOP_PRODUCTS: ShopProduct[] = [
  { sku: 'pack:pastel', kind: 'pens', name: 'Pastel Pen Pack', note: 'Four soft pastel inks: pink, blue, mint and lemon.', priceTHB: 29, grants: ['pen:pastel_pink', 'pen:pastel_blue', 'pen:pastel_mint', 'pen:pastel_lemon'] },
  { sku: 'pack:neon', kind: 'pens', name: 'Neon Pen Pack', note: 'Four glowing neon inks that pop off the page.', priceTHB: 39, grants: ['pen:neon_pink', 'pen:neon_orange', 'pen:neon_green', 'pen:electric_blue'] },
  { sku: 'pack:galaxy', kind: 'pens', name: 'Galaxy Pen Pack', note: 'Four cosmic inks for stars, planets and space monsters.', priceTHB: 49, grants: ['pen:galaxy_purple', 'pen:starlight', 'pen:nebula_pink', 'pen:deep_space'] },
  { sku: 'pack:rainbow', kind: 'pens', name: 'RGB Rainbow Pens', note: 'Two magic inks that change colour with every stroke: Rainbow and Pastel Rainbow. Only here.', priceTHB: 39, grants: ['pen:rainbow', 'pen:pastel_rainbow'] },
  // Themed sets: the drawing stamps and inks can also be won in the fortune teller; each set has one thing only sold here.
  { sku: 'set:princess', kind: 'set', name: 'Princess Set', note: 'Tiara, party dress, star wand and heart stamps, Lilac ink, the Princess Win stamp and the set-only Princess Pink pen.', priceTHB: 29, grants: ['brush:tiara', 'brush:dress', 'brush:star_wand', 'brush:heart', 'pen:lilac', 'stamp:princess', 'pen:princess_pink'] },
  { sku: 'set:knight', kind: 'set', name: 'Knight Set', note: 'Sword, shield, battle axe, bow and hammer stamps, Ruby ink and the set-only Steel pen.', priceTHB: 29, grants: ['brush:sword', 'brush:shield', 'brush:axe', 'brush:bow', 'brush:hammer', 'pen:ruby', 'pen:steel'] },
  { sku: 'set:pets', kind: 'set', name: 'Cute Pets Set', note: 'Cat ears, bunny ears, ribbon bow and heart glasses stamps, Bubblegum and Peach inks and the So Cute stamp.', priceTHB: 29, grants: ['brush:cat_ears', 'brush:bunny_ears', 'brush:ribbon', 'brush:heart_glasses', 'pen:bubblegum', 'pen:peach', 'stamp:cutest'] },
  { sku: 'set:cafe', kind: 'set', name: 'Sweet Cafe Set', note: 'Bubble tea, cupcake, strawberry and donut stamps, and Milk Tea, Caramel and Sakura inks.', priceTHB: 29, grants: ['brush:bubble_tea', 'brush:cupcake', 'brush:strawberry', 'brush:donut', 'pen:milk_tea', 'pen:caramel', 'pen:sakura'] },
  { sku: 'set:magic', kind: 'set', name: 'Magical Girl Set', note: 'Magic staff, potion, moon, butterfly and unicorn horn stamps, Amethyst ink and the Unicorn Win stamp.', priceTHB: 29, grants: ['brush:magic_staff', 'brush:potion', 'brush:moon', 'brush:butterfly', 'brush:unicorn_horn', 'pen:amethyst', 'stamp:unicorn_win'] },
  { sku: 'set:fashion', kind: 'set', name: 'Fashionista Set', note: 'Pleated skirt, high heel, handbag, lipstick and round glasses stamps, Rose Gold ink and the Superstar stamp.', priceTHB: 29, grants: ['brush:skirt', 'brush:high_heel', 'brush:handbag', 'brush:lipstick', 'brush:round_glasses', 'pen:rose_gold', 'stamp:superstar'] },
  // One more hero slot each (the game offers the next one; coins can unlock them too).
  { sku: 'slot:hero3', kind: 'slot', name: 'Hero Slot 3', note: 'Room for a third hero, right away.', priceTHB: 19, grants: ['heroslot:3'] },
  { sku: 'slot:hero4', kind: 'slot', name: 'Hero Slot 4', note: 'Room for a fourth hero, right away.', priceTHB: 19, grants: ['heroslot:4'] },
  { sku: 'slot:hero5', kind: 'slot', name: 'Hero Slot 5', note: 'Room for a fifth hero, right away.', priceTHB: 19, grants: ['heroslot:5'] },
  { sku: 'slot:hero6', kind: 'slot', name: 'Hero Slot 6', note: 'Room for a sixth hero, right away.', priceTHB: 19, grants: ['heroslot:6'] },
  { sku: 'stamp:royal', kind: 'stamp', name: 'Royal Win Stamp', note: 'A golden ROYAL WIN on every victory.', priceTHB: 39, grants: ['stamp:royal'] },
  { sku: 'stamp:dragon', kind: 'stamp', name: 'Dragon Slayer Stamp', note: 'A green DRAGON SLAYER on every victory.', priceTHB: 39, grants: ['stamp:dragon'] },
  { sku: 'stamp:lightning', kind: 'stamp', name: 'Lightning Stamp', note: 'A crackling LIGHTNING on every victory.', priceTHB: 39, grants: ['stamp:lightning'] },
  { sku: 'support:bronze', kind: 'support', name: 'Bronze Supporter', note: 'Thank you! A bronze supporter badge and the Thank You stamp.', priceTHB: 35, grants: ['supporter:1', 'stamp:thank_you'] },
  { sku: 'support:silver', kind: 'support', name: 'Silver Supporter', note: 'A silver badge, the Thank You stamp and the Supporter Star stamp.', priceTHB: 99, grants: ['supporter:2', 'stamp:thank_you', 'stamp:supporter_star'] },
  { sku: 'support:gold', kind: 'support', name: 'Gold Supporter', note: 'A gold badge, both supporter stamps and the Supporter Gold pen.', priceTHB: 199, grants: ['supporter:3', 'stamp:thank_you', 'stamp:supporter_star', 'pen:supporter_gold'] },
  { sku: 'support:diamond', kind: 'support', name: 'Diamond Supporter', note: 'The diamond badge and every supporter reward. You make this game possible!', priceTHB: 499, grants: ['supporter:4', 'stamp:thank_you', 'stamp:supporter_star', 'pen:supporter_gold', 'stamp:diamond'] },
  // Ready-made heroes (also sold for coins); the game offers each one once its art is delivered.
  ...HERO_LOOKS.map((l): ShopProduct => ({ sku: `hero:${l.id}`, kind: 'look', name: l.name, note: l.note, priceTHB: LOOK_THB, grants: [lookItem(l.id)] })),
];

export function shopProduct(sku: string): ShopProduct | undefined {
  return SHOP_PRODUCTS.find((p) => p.sku === sku);
}

/** The highest supporter badge level among owned items (0 = none). */
export function supporterLevel(owned: readonly string[]): number {
  return owned.reduce((best, id) => (id.startsWith('supporter:') ? Math.max(best, Number(id.slice(10)) || 0) : best), 0);
}

/* ---------- REST DTOs ---------- */

/** GET /api/shop: enabled is false until Stripe is set up on the server. */
export interface ShopResponse {
  enabled: boolean;
  products: ShopProduct[];
  /** Items this player owns (empty without a token). */
  owned: string[];
}
export interface CheckoutRequest {
  sku: string;
}
/** POST /api/shop/checkout: send the player to Stripe's payment page. */
export interface CheckoutResponse {
  url: string;
  id: string;
}
export type PurchaseStatus = 'pending' | 'paid' | 'failed';
/** GET /api/shop/purchases/{id}: after returning from Stripe (PromptPay may stay pending a while). */
export interface PurchaseResponse {
  id: string;
  sku: string;
  status: PurchaseStatus;
  owned: string[];
}
